import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Toggle } from '../../components/ui/Toggle';
import { Badge } from '../../components/ui/Badge';
import { Settings, Database, Mail, Shield, Zap, Bell, Globe, Save, AlertCircle } from 'lucide-react';
export function SystemSettingsPage() {
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Settings state
  const [settings, setSettings] = useState({
    maintenanceMode: false,
    newBuilderSignups: true,
    emailNotifications: true,
    telemetryTracking: true,
    autoBackups: true,
    sessionTimeout: '30',
    maxFileSize: '10',
    apiRateLimit: '1000',
    defaultProjectQuota: '10',
    emailProvider: 'sendgrid',
    storageProvider: 'aws-s3'
  });
  const handleToggle = (key: string, value: boolean) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setUnsavedChanges(true);
  };
  const handleInputChange = (key: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setUnsavedChanges(true);
  };
  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setUnsavedChanges(false);
    }, 1000);
  };
  return <div className="space-y-6 max-w-5xl mx-auto" data-id="element-809">
      <div className="flex items-center justify-between" data-id="element-810">
        <div data-id="element-811">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-812">System Settings</h1>
          <p className="text-slate-500" data-id="element-813">
            Configure platform-wide settings and preferences
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-814">
          {unsavedChanges && <span className="text-sm text-amber-600 font-medium flex items-center bg-amber-50 px-3 py-1 rounded-full" data-id="element-815">
              <AlertCircle className="h-4 w-4 mr-2" data-id="element-816" />
              Unsaved Changes
            </span>}
          <Button onClick={handleSave} isLoading={isSaving} disabled={!unsavedChanges} leftIcon={<Save className="h-4 w-4" data-id="element-818" />} data-id="element-817">
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Platform Settings */}
      <Card data-id="element-819">
        <CardHeader data-id="element-820">
          <div className="flex items-center gap-2" data-id="element-821">
            <Settings className="h-5 w-5 text-slate-600" data-id="element-822" />
            <CardTitle data-id="element-823">Platform Settings</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6" data-id="element-824">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-825">
            <div className="flex-1" data-id="element-826">
              <p className="font-medium text-slate-900" data-id="element-827">Maintenance Mode</p>
              <p className="text-sm text-slate-500" data-id="element-828">
                Temporarily disable platform access for all users
              </p>
            </div>
            <Toggle checked={settings.maintenanceMode} onChange={checked => handleToggle('maintenanceMode', checked)} data-id="element-829" />
          </div>

          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-830">
            <div className="flex-1" data-id="element-831">
              <p className="font-medium text-slate-900" data-id="element-832">New Builder Signups</p>
              <p className="text-sm text-slate-500" data-id="element-833">
                Allow new builder companies to sign up
              </p>
            </div>
            <Toggle checked={settings.newBuilderSignups} onChange={checked => handleToggle('newBuilderSignups', checked)} data-id="element-834" />
          </div>

          <div className="flex items-center justify-between py-3" data-id="element-835">
            <div className="flex-1" data-id="element-836">
              <p className="font-medium text-slate-900" data-id="element-837">Session Timeout</p>
              <p className="text-sm text-slate-500" data-id="element-838">
                Automatic logout after inactivity (minutes)
              </p>
            </div>
            <input type="number" value={settings.sessionTimeout} onChange={e => handleInputChange('sessionTimeout', e.target.value)} className="w-24 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-839" />
          </div>
        </CardContent>
      </Card>

      {/* Data & Storage */}
      <Card data-id="element-840">
        <CardHeader data-id="element-841">
          <div className="flex items-center gap-2" data-id="element-842">
            <Database className="h-5 w-5 text-slate-600" data-id="element-843" />
            <CardTitle data-id="element-844">Data & Storage</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6" data-id="element-845">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-846">
            <div className="flex-1" data-id="element-847">
              <p className="font-medium text-slate-900" data-id="element-848">Automatic Backups</p>
              <p className="text-sm text-slate-500" data-id="element-849">
                Daily automated database backups
              </p>
            </div>
            <Toggle checked={settings.autoBackups} onChange={checked => handleToggle('autoBackups', checked)} data-id="element-850" />
          </div>

          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-851">
            <div className="flex-1" data-id="element-852">
              <p className="font-medium text-slate-900" data-id="element-853">Max File Upload Size</p>
              <p className="text-sm text-slate-500" data-id="element-854">Maximum file size in MB</p>
            </div>
            <input type="number" value={settings.maxFileSize} onChange={e => handleInputChange('maxFileSize', e.target.value)} className="w-24 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-855" />
          </div>

          <div className="flex items-center justify-between py-3" data-id="element-856">
            <div className="flex-1" data-id="element-857">
              <p className="font-medium text-slate-900" data-id="element-858">Storage Provider</p>
              <p className="text-sm text-slate-500" data-id="element-859">Cloud storage service</p>
            </div>
            <select value={settings.storageProvider} onChange={e => handleInputChange('storageProvider', e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-860">
              <option value="aws-s3" data-id="element-861">AWS S3</option>
              <option value="google-cloud" data-id="element-862">Google Cloud Storage</option>
              <option value="azure" data-id="element-863">Azure Blob Storage</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Email & Notifications */}
      <Card data-id="element-864">
        <CardHeader data-id="element-865">
          <div className="flex items-center gap-2" data-id="element-866">
            <Mail className="h-5 w-5 text-slate-600" data-id="element-867" />
            <CardTitle data-id="element-868">Email & Notifications</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6" data-id="element-869">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-870">
            <div className="flex-1" data-id="element-871">
              <p className="font-medium text-slate-900" data-id="element-872">Email Notifications</p>
              <p className="text-sm text-slate-500" data-id="element-873">
                Send system notifications via email
              </p>
            </div>
            <Toggle checked={settings.emailNotifications} onChange={checked => handleToggle('emailNotifications', checked)} data-id="element-874" />
          </div>

          <div className="flex items-center justify-between py-3" data-id="element-875">
            <div className="flex-1" data-id="element-876">
              <p className="font-medium text-slate-900" data-id="element-877">Email Provider</p>
              <p className="text-sm text-slate-500" data-id="element-878">Email delivery service</p>
            </div>
            <select value={settings.emailProvider} onChange={e => handleInputChange('emailProvider', e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-879">
              <option value="sendgrid" data-id="element-880">SendGrid</option>
              <option value="mailgun" data-id="element-881">Mailgun</option>
              <option value="aws-ses" data-id="element-882">AWS SES</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Security & API */}
      <Card data-id="element-883">
        <CardHeader data-id="element-884">
          <div className="flex items-center gap-2" data-id="element-885">
            <Shield className="h-5 w-5 text-slate-600" data-id="element-886" />
            <CardTitle data-id="element-887">Security & API</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6" data-id="element-888">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-889">
            <div className="flex-1" data-id="element-890">
              <p className="font-medium text-slate-900" data-id="element-891">API Rate Limit</p>
              <p className="text-sm text-slate-500" data-id="element-892">
                Requests per hour per API key
              </p>
            </div>
            <input type="number" value={settings.apiRateLimit} onChange={e => handleInputChange('apiRateLimit', e.target.value)} className="w-32 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-893" />
          </div>

          <div className="flex items-center justify-between py-3" data-id="element-894">
            <div className="flex-1" data-id="element-895">
              <p className="font-medium text-slate-900" data-id="element-896">
                Default Project Quota
              </p>
              <p className="text-sm text-slate-500" data-id="element-897">
                Max projects per new builder
              </p>
            </div>
            <input type="number" value={settings.defaultProjectQuota} onChange={e => handleInputChange('defaultProjectQuota', e.target.value)} className="w-24 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-898" />
          </div>
        </CardContent>
      </Card>

      {/* Analytics & Telemetry */}
      <Card data-id="element-899">
        <CardHeader data-id="element-900">
          <div className="flex items-center gap-2" data-id="element-901">
            <Zap className="h-5 w-5 text-slate-600" data-id="element-902" />
            <CardTitle data-id="element-903">Analytics & Telemetry</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6" data-id="element-904">
          <div className="flex items-center justify-between py-3" data-id="element-905">
            <div className="flex-1" data-id="element-906">
              <p className="font-medium text-slate-900" data-id="element-907">Telemetry Tracking</p>
              <p className="text-sm text-slate-500" data-id="element-908">
                Collect usage analytics and event data
              </p>
            </div>
            <Toggle checked={settings.telemetryTracking} onChange={checked => handleToggle('telemetryTracking', checked)} data-id="element-909" />
          </div>
        </CardContent>
      </Card>
    </div>;
}